import api from './api';

export type NativeIapProvider = 'apple' | 'google';

export interface NativeIapUserRef {
  id: number;
  name: string;
  surname: string | null;
  email: string;
}

export interface NativeIapTransaction {
  id: number;
  userId: number;
  user: NativeIapUserRef;
  subscriptionId: number | null;
  orderReference: string;
  amount: string;
  currency: string;
  status: string;
  type: string;
  providerResponse: string | null;
  createdAt: string;
}

export interface NativeIapSubscription {
  id: number;
  userId: number;
  user: NativeIapUserRef;
  plan: { id: number; name: string; code: string } | null;
  billingCycle: 'month' | 'year';
  status: string;
  isTrial: boolean;
  appleOriginalTransactionId: string | null;
  appleEnvironment: 'sandbox' | 'production' | null;
  googlePurchaseToken: string | null;
  googleEnvironment: 'sandbox' | 'production' | null;
  nextPaymentDate: string | null;
  lastPaymentDate: string | null;
  createdAt: string;
}

export interface NativeIapWebhookLog {
  id: number;
  orderReference: string | null;
  payload: unknown;
  status: string;
  errorMessage: string | null;
  createdAt: string;
}

export interface NativeIapAttempt {
  id: number;
  userId: number | null;
  provider: NativeIapProvider;
  success: boolean;
  reason: string | null;
  errorMessage: string | null;
  rawIdentifier: string | null;
  productId: string | null;
  createdAt: string;
}

export interface NativeIapHealth {
  // Apple
  bundleId?: string;
  hasSigningCredentials?: boolean;
  rootCertificatesLoaded?: boolean;
  rootCertificatesCount?: number;
  productionVerificationEnabled?: boolean;
  // Google
  packageName?: string;
  androidPublisherConfigured?: boolean;
  rtdnConfigured?: boolean;
}

export interface PageParams {
  limit?: number;
  offset?: number;
}

export interface PagedResult<T> {
  data: T[];
  meta: { total: number; limit: number; offset: number };
}

/**
 * Read-only debug views shared across native IAP providers (Apple/Google) —
 * DEVELOPER/SUPERADMIN. Backend:
 * api/src/modules/client-payments/native-iap/native-iap-admin.controller.ts
 * Was Apple-only (apple-iap-admin.service.ts, hit /admin/client-payments/apple/*,
 * a backend route that no longer exists) — see
 * workspace/current-work/google-play-billing-android.md.
 */
/**
 * Бекенд-контролер нічого не загортає сам (повертає {data, meta} напряму) —
 * загортання в конверт {success, data, meta} робить глобальний
 * ResponseInterceptor (api/src/common/interceptors/response.interceptor.ts).
 * Тому справжній payload — це `response.data.data`, а не `response.data`.
 */
export const nativeIapAdminService = {
  getTransactions: async (provider: NativeIapProvider, params: PageParams = {}) => {
    const res = await api.get('/admin/client-payments/native-iap/transactions', { params: { provider, ...params } });
    return res.data.data as PagedResult<NativeIapTransaction>;
  },

  getSubscriptions: async (provider: NativeIapProvider, params: PageParams = {}) => {
    const res = await api.get('/admin/client-payments/native-iap/subscriptions', { params: { provider, ...params } });
    return res.data.data as PagedResult<NativeIapSubscription>;
  },

  getWebhookLogs: async (provider: NativeIapProvider, params: PageParams = {}) => {
    const res = await api.get('/admin/client-payments/native-iap/webhook-logs', { params: { provider, ...params } });
    return res.data.data as PagedResult<NativeIapWebhookLog>;
  },

  getAttempts: async (provider: NativeIapProvider, params: PageParams = {}) => {
    const res = await api.get('/admin/client-payments/native-iap/attempts', { params: { provider, ...params } });
    return res.data.data as PagedResult<NativeIapAttempt>;
  },

  getHealth: async (provider: NativeIapProvider) => {
    const res = await api.get('/admin/client-payments/native-iap/health', { params: { provider } });
    return res.data.data as NativeIapHealth;
  },
};
